import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Crown, Shield, ShieldCheck, Sparkles } from 'lucide-react';
import { fmtETDate } from '../../utils/tz';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_INFO = {
  basic: { icon: Shield, label: 'Basic', color: '#A3A3A3' },
  protect: { icon: ShieldCheck, label: 'Protect', color: '#D4AF37' },
  premier: { icon: Sparkles, label: 'Premier', color: '#F3E5AB' },
};

const Members = () => {
  const [memberships, setMemberships] = useState([]);

  useEffect(() => {
    axios.get(`${API}/memberships/active`, { withCredentials: true })
      .then(({ data }) => setMemberships(data))
      .catch(console.error);
  }, []);

  const byTier = memberships.reduce((acc, m) => {
    acc[m.tier] = (acc[m.tier] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="members-page" className="space-y-8">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Members</h2>
        <p className="text-[#A3A3A3]">Active membership holders</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#141414] border border-[#D4AF37] rounded-md p-5">
          <div className="flex items-center gap-2 mb-2">
            <Crown className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-[#A3A3A3] text-xs uppercase tracking-wider">Total Members</span>
          </div>
          <p className="text-white text-4xl font-bold font-['Outfit']">{memberships.length}</p>
        </div>
        {['basic', 'protect', 'premier'].map((tier) => {
          const Icon = TIER_INFO[tier].icon;
          return (
            <div key={tier} className="bg-[#141414] border border-[#262626] rounded-md p-5">
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-5 h-5" style={{ color: TIER_INFO[tier].color }} />
                <span className="text-[#A3A3A3] text-xs uppercase tracking-wider">{TIER_INFO[tier].label}</span>
              </div>
              <p className="text-white text-4xl font-bold font-['Outfit']">{byTier[tier] || 0}</p>
            </div>
          );
        })}
      </div>

      {/* List */}
      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#0A0A0A] border-b border-[#262626]">
            <tr>
              <th className="text-left px-6 py-4 text-[#A3A3A3] text-xs uppercase tracking-wider">Customer</th>
              <th className="text-left px-6 py-4 text-[#A3A3A3] text-xs uppercase tracking-wider">Tier</th>
              <th className="text-left px-6 py-4 text-[#A3A3A3] text-xs uppercase tracking-wider">Billing</th>
              <th className="text-left px-6 py-4 text-[#A3A3A3] text-xs uppercase tracking-wider">Start Date</th>
              <th className="text-right px-6 py-4 text-[#A3A3A3] text-xs uppercase tracking-wider">Fee</th>
            </tr>
          </thead>
          <tbody>
            {memberships.length === 0 ? (
              <tr><td colSpan={5} className="text-center px-6 py-12 text-[#A3A3A3]">No active members yet</td></tr>
            ) : memberships.map((m) => {
              const tier = TIER_INFO[m.tier] || TIER_INFO.basic;
              const Icon = tier.icon;
              return (
                <tr key={m.id} data-testid={`member-row-${m.id}`} className="border-b border-[#262626] last:border-0 hover:bg-[#1A1A1A]">
                  <td className="px-6 py-4 text-white font-medium">{m.customer_name}</td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-[#262626]">
                      <Icon className="w-3 h-3" style={{ color: tier.color }} />
                      <span className="text-white text-xs font-bold uppercase">{tier.label}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 text-[#A3A3A3] capitalize">{m.billing_period || 'annual'}</td>
                  <td className="px-6 py-4 text-[#A3A3A3] text-sm">{fmtETDate(m.start_date)}</td>
                  <td className="px-6 py-4 text-right text-[#D4AF37] font-bold">${m.monthly_fee?.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Members;
